export type MapLayerSource = 'public' | 'org' | 'preview' | 'internal';

type MapLayerCatalogItemBase = {
  id: string;
  name: string;
  source: MapLayerSource;
  visible: boolean;
  loadError: boolean;
};

/** `layer` is the original object the catalog was built from. */
export type MapLayerCatalogItem<E = unknown, I = unknown> =
  | (MapLayerCatalogItemBase & { kind: 'External'; layer: E })
  | (MapLayerCatalogItemBase & { kind: 'Internal'; layer: I });

type CatalogLayer = { id: string | number; name?: unknown };

/** Display order of the groups in the layer picker. */
export const MAP_LAYER_SOURCE_ORDER: MapLayerSource[] = [
  'org',
  'public',
  'preview',
  'internal',
];

export const MAP_LAYER_SOURCE_LABELS: Record<MapLayerSource, string> = {
  org: 'Organization',
  public: 'Public',
  preview: 'Previews',
  internal: 'Internal layers',
};

const layerName = (layer: CatalogLayer): string =>
  (typeof layer.name === 'string' && layer.name.trim()) || 'Untitled layer';

/**
 * Every layer in scope for the map, deduped by id. An external layer listed in
 * several sources keeps the first one (public, then org, then preview),
 * matching how the map adds them. Sorted by group order, then name.
 */
export function buildMapLayerCatalog<
  E extends CatalogLayer,
  I extends CatalogLayer,
  P extends CatalogLayer = E,
>(params: {
  publicLayers: readonly E[];
  orgLayers: readonly E[];
  previewLayers: readonly P[];
  internalLayers: readonly I[];
  isExternalVisible: (id: string) => boolean;
  isInternalVisible: (id: string) => boolean;
  externalLoadErrors?: ReadonlyMap<string, boolean>;
}): MapLayerCatalogItem<E | P, I>[] {
  const items: MapLayerCatalogItem<E | P, I>[] = [];
  const seenExternal = new Set<string>();
  const externalSources: [MapLayerSource, readonly (E | P)[]][] = [
    ['public', params.publicLayers],
    ['org', params.orgLayers],
    ['preview', params.previewLayers],
  ];
  for (const [source, layers] of externalSources) {
    for (const layer of layers) {
      const id = String(layer.id);
      if (seenExternal.has(id)) continue;
      seenExternal.add(id);
      items.push({
        id,
        name: layerName(layer),
        kind: 'External',
        source,
        visible: params.isExternalVisible(id),
        loadError: params.externalLoadErrors?.get(id) ?? false,
        layer,
      });
    }
  }
  const seenInternal = new Set<string>();
  for (const layer of params.internalLayers) {
    const id = String(layer.id);
    if (seenInternal.has(id)) continue;
    seenInternal.add(id);
    items.push({
      id,
      name: layerName(layer),
      kind: 'Internal',
      source: 'internal',
      visible: params.isInternalVisible(id),
      loadError: false,
      layer,
    });
  }
  const rank = (source: MapLayerSource) =>
    MAP_LAYER_SOURCE_ORDER.indexOf(source);
  return items.sort(
    (a, b) =>
      rank(a.source) - rank(b.source) ||
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}

/** Catalog entries grouped by source in display order; empty groups are omitted. */
export function groupMapLayerCatalog<T extends MapLayerCatalogItem>(
  items: readonly T[]
): { source: MapLayerSource; label: string; items: T[] }[] {
  return MAP_LAYER_SOURCE_ORDER.map((source) => ({
    source,
    label: MAP_LAYER_SOURCE_LABELS[source],
    items: items.filter((item) => item.source === source),
  })).filter((group) => group.items.length > 0);
}

/** Case-insensitive match on name, kind, or group label. */
export function filterMapLayerCatalog<T extends MapLayerCatalogItem>(
  items: readonly T[],
  query: string
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...items];
  return items.filter((item) =>
    [item.name, item.kind, MAP_LAYER_SOURCE_LABELS[item.source]].some((text) =>
      text.toLowerCase().includes(q)
    )
  );
}
